import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Communities from "./pages/Communities";
import CommunityDetail from "./pages/CommunityDetail";
import CreateCommunity from "./pages/CreateCommunity";
import Simulator from "./pages/Simulator";
import EnergyFlows from "./pages/EnergyFlows";
import Forecasts from "./pages/Forecasts";
import Alerts from "./pages/Alerts";
import CommunityMap from "./pages/CommunityMap";
import CommunityChat from "./pages/CommunityChat";
import LinkyPage from "./pages/LinkyPage";
import WalletPage from "./pages/WalletPage";
import SepaPage from "./pages/SepaPage";
import AdminDashboard from "./pages/AdminDashboard";
import ProfilePage from "./pages/ProfilePage";
import ProDashboard from "./pages/ProDashboard";
import ClientConsumption from "./pages/ClientConsumption";
import LiveSimulation from "./pages/LiveSimulation";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import LegalNotices from "./pages/LegalNotices";
import TermsOfService from "./pages/TermsOfService";
import TermsOfSale from "./pages/TermsOfSale";
import EnedisMandate from "./pages/EnedisMandate";
import { CookieConsent } from "./components/CookieConsent";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/confidentialite" element={<PrivacyPolicy />} />
      <Route path="/mentions-legales" element={<LegalNotices />} />
      <Route path="/cgu" element={<TermsOfService />} />
      <Route path="/cgv" element={<TermsOfSale />} />
      <Route path="/communities" element={<Communities />} />
      <Route path="/communities/:id" element={<CommunityDetail />} />
      <Route path="/simulator" element={<Simulator />} />
      <Route path="/map" element={<CommunityMap />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/communities/create" element={<ProtectedRoute><CreateCommunity /></ProtectedRoute>} />
      <Route path="/communities/:communityId/flows" element={<ProtectedRoute><EnergyFlows /></ProtectedRoute>} />
      <Route path="/communities/:communityId/chat" element={<ProtectedRoute><CommunityChat /></ProtectedRoute>} />
      <Route path="/forecasts" element={<ProtectedRoute><Forecasts /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/linky" element={<ProtectedRoute><LinkyPage /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/sepa" element={<ProtectedRoute><SepaPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/pro" element={<ProtectedRoute><ProDashboard /></ProtectedRoute>} />
      <Route path="/consumption" element={<ProtectedRoute><ClientConsumption /></ProtectedRoute>} />
      <Route path="/live" element={<LiveSimulation />} />
      <Route path="/mandat-enedis" element={<ProtectedRoute><EnedisMandate /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <CookieConsent />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: 'white',
                border: '1px solid hsl(142 77% 73%)',
              },
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
